import { tokenizersApi } from '../api/smartTokenizerApi';
import { CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID } from './standardContractTemplates';
import { toEthereumInteropHex } from './coreIcanAddress';
import { sanitizeContractName } from './offlineSigning';

function deriveSymbolFromAssetClass(ac) {
  if (ac?.marketSymbol && String(ac.marketSymbol).trim()) {
    return String(ac.marketSymbol).trim().slice(0, 12);
  }
  const code = String(ac?.code || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return (code.slice(0, 6) || 'RWA').slice(0, 12);
}

function dedupeWalletStrings(...wallets) {
  const out = [];
  const seen = new Set();
  for (const raw of wallets) {
    const t = String(raw || '').trim();
    if (!t) continue;
    const low = t.toLowerCase();
    if (seen.has(low)) continue;
    seen.add(low);
    out.push(t);
  }
  return out;
}

/**
 * Constructor args for RwaSharedSeriesErc721: (string name, string symbol, address[] initialMinters).
 * Those addresses are **operators** for both mint and burn on the reference CBC721 artifact.
 *
 * Preloaded CBC20 / CBC1155 use the same three constructor args. For ERC-777 / ERC-4626 / ERC-3525 or other
 * custom bytecode, mirror the same idea
 * (one owner + `minters(address)` or `operators(address)` + `setMinter`/`setOperator`) so the API can
 * enforce catalog access consistently.
 */
export async function buildSharedSeries721ConstructorParameters({
  contractTemplateId,
  isAdmin,
  isPlatformStandard,
  assetClass,
  contractName,
  /** For non–platform-catalog CBC721: wallets to seed as on-chain minters (deploy tx signer is owner; mint may use the same or another approved operator). */
  tokenizerWalletAddresses = []
}) {
  if (contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) {
    return [];
  }
  const name = sanitizeContractName(contractName || `${assetClass?.name || 'RWA'}NFT`);
  const symbol = deriveSymbolFromAssetClass(assetClass);
  let minters = [];
  if (isAdmin && isPlatformStandard) {
    try {
      const { data } = await tokenizersApi.list();
      const list = Array.isArray(data) ? data : [];
      const approved = list.filter((t) => {
        const st = t.status ?? t.Status;
        return String(st) === 'Approved' && String(t.walletAddress || t.WalletAddress || '').trim();
      });
      const collected = [];
      for (const t of approved) {
        collected.push(String(t.walletAddress || t.WalletAddress || '').trim());
      }
      minters = dedupeWalletStrings(...collected);
    } catch {
      minters = [];
    }
  } else {
    const seeds = Array.isArray(tokenizerWalletAddresses) ? tokenizerWalletAddresses : [];
    minters = dedupeWalletStrings(...seeds);
    if (minters.length === 0) {
      throw new Error(
        'CBC721 constructor needs at least one initial minter address. Set your offline signer address and ensure your authorized tokenizer wallet is loaded, then prepare deployment again.'
      );
    }
  }
  const mintersForAbi = minters.map((a) => toEthereumInteropHex(a));
  return [name, symbol, JSON.stringify(mintersForAbi)];
}
